import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import ZSLICCampaing from "@/services/zslic-campaign";
import { useUserStore } from "./user";

export const useAdvertisingStore = defineStore("advertising", () => {
  // State
  const campaigns = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const lastFetch = ref(null);
  const currentPage = ref("");
  const currentLang = ref("");
  const environment = ref("production");

  const userStore = useUserStore();
  const { token } = storeToRefs(userStore);
  
  const zslicCampaingService = new ZSLICCampaing();

  // Getters
  const hasCampaigns = computed(() => campaigns.value.length > 0);
  const isLoading = computed(() => loading.value);
  const hasError = computed(() => error.value !== null);
  const activeCampaigns = computed(() => {
    const now = new Date();
    return campaigns.value.filter(campaign => {
      const startDate = new Date(campaign.start_date);
      const endDate = new Date(campaign.end_date);
      return now >= startDate && now <= endDate;
    });
  });

  // Actions
  async function fetchCampaigns(lang="pt-pt") {


    loading.value = true;
    error.value = null;
    // currentPage.value = page;
    currentLang.value = lang;

    try {
      const {data} = await zslicCampaingService.getZSLICCampaings(token.value, lang);
      
      
      campaigns.value = Array.isArray(data) ? data : [];
      lastFetch.value = new Date();
      
      return campaigns.value;
    } catch (err) {
      error.value = err.message;
      campaigns.value = [];
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function refreshCampaigns() {
    if (currentPage.value && currentLang.value) {
      return await fetchCampaigns(currentPage.value, currentLang.value);
    }
    throw new Error('No previous fetch parameters available');
  }

  function clearCampaigns() {
    campaigns.value = [];
    error.value = null;
    lastFetch.value = null;
    currentPage.value = "";
    currentLang.value = "";
  }

  function clearError() {
    error.value = null;
  }

  function setEnvironment(env) {
    environment.value = env;
    zslicCampaingService.setEnvironment?.(env);
  }

  // Helper function to get campaigns by specific criteria
  function getCampaignsByDateRange(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return campaigns.value.filter(campaign => {
      const campaignStart = new Date(campaign.start_date);
      const campaignEnd = new Date(campaign.end_date);
      
      return (campaignStart >= start && campaignStart <= end) ||
             (campaignEnd >= start && campaignEnd <= end) ||
             (campaignStart <= start && campaignEnd >= end);
    });
  }

  function getCampaignById(id) {
    return campaigns.value.find(campaign => campaign.id === id);
  }

  // Cache management
  function shouldRefresh(maxAge =  60 * 60 * 1000) { // 1 hour default
    if (!lastFetch.value) return true;
    return (new Date() - lastFetch.value) > maxAge;
  }

  return {
    // State
    campaigns,
    loading,
    error,
    lastFetch,
    currentPage,
    currentLang,
    environment,
    
    // Getters
    hasCampaigns,
    isLoading,
    hasError,
    activeCampaigns,
    
    // Actions
    fetchCampaigns,
    refreshCampaigns,
    clearCampaigns,
    clearError,
    setEnvironment,
    getCampaignsByDateRange,
    getCampaignById,
    shouldRefresh
  };
});
